import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, ClipboardList, PenTool, CheckCircle2, Clock, Layers, Timer, Award, GraduationCap, Globe2, Stethoscope, ScanFace, FlaskConical, Factory, ShieldCheck } from "lucide-react";
import { FadeIn, SectionTag } from "./Reveal";

const VIEWER_IMG = "https://static.prod-images.emergentagent.com/jobs/4f24f9fb-348c-474e-99ca-a0ad8f55963c/images/6c259f783a43dacb3df5da2259d03044df043c3e2728e0690ba3a4cf3e115326.jpeg";

const PAINS = [
  { icon: Clock, t: "Hours per digital setup", d: "Staging, attachments and IPR planned tooth-by-tooth eats into chairside time you can't get back." },
  { icon: Layers, t: "Software learning curve", d: "Every platform has its own conventions — mastering them all is a job in itself." },
  { icon: Timer, t: "Revisions pile up", d: "Setups that ignore biomechanics come back as refinements, remakes and unhappy patients." },
  { icon: ClipboardList, t: "Volume doesn't scale", d: "More aligner cases shouldn't mean more late nights in planning software." },
];

export function ProblemSolution() {
  return (
    <>
      <section className="py-24 lg:py-32" data-testid="problem-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionTag>The Problem</SectionTag>
          <FadeIn>
            <h2 className="font-display font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl max-w-3xl leading-[1.02]">
              Stop spending hours building <span className="text-neon">every digital setup.</span>
            </h2>
          </FadeIn>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {PAINS.map((p, i) => (
              <FadeIn key={p.t} delay={i * 0.07} className="h-full">
                <div className="h-full bg-white/5 border border-white/10 rounded-2xl p-6 lg:p-7 transition-colors duration-500 hover:border-neon/30">
                  <p.icon className="h-6 w-6 text-neon mb-4" />
                  <p className="font-display font-bold text-lg">{p.t}</p>
                  <p className="text-xs text-slate-500 mt-2 leading-relaxed">{p.d}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-ink-2/40" data-testid="solution-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionTag>The Solution</SectionTag>
          <FadeIn>
            <h2 className="font-display font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl max-w-3xl leading-[1.02]">
              Your clinical direction.<br /><span className="text-neon">Our planning expertise.</span>
            </h2>
          </FadeIn>
          <FadeIn delay={0.1}>
            <p className="mt-6 max-w-2xl text-slate-400 text-sm md:text-base leading-relaxed">
              OrthoPlaniX doesn't replace the dentist — it extends your digital orthodontic capacity. Your patients are yours. Your clinical decisions are yours. We provide the specialist planning behind them.
            </p>
          </FadeIn>
          <div className="mt-16 grid md:grid-cols-3 gap-px bg-white/10 rounded-2xl overflow-hidden border border-white/10">
            {[
              { icon: ClipboardList, n: "01", t: "You prescribe", d: "Diagnosis, treatment objectives and your clinical preferences — expansion vs IPR, staging velocity, attachment style." },
              { icon: PenTool, n: "02", t: "We plan", d: "Specialist planners build the digital setup: staging, attachments, IPR and bite-correction strategy — reviewed clinically before it reaches you." },
              { icon: CheckCircle2, n: "03", t: "You approve", d: "Review every movement in 3D, request revisions, and approve digitally. Nothing goes to production without your sign-off." },
            ].map((s, i) => (
              <FadeIn key={s.n} delay={i * 0.1} className="h-full">
                <div className="h-full bg-ink p-8 lg:p-10 transition-colors duration-500 hover:bg-ink-2">
                  <div className="flex items-center justify-between">
                    <s.icon className="h-7 w-7 text-neon" />
                    <span className="font-display font-black text-5xl text-neon/15" aria-hidden="true">{s.n}</span>
                  </div>
                  <p className="font-display font-bold text-2xl mt-6">{s.t}</p>
                  <p className="text-sm text-slate-400 mt-3 leading-relaxed">{s.d}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

export function ViewerShowcase() {
  return (
    <section className="py-24 lg:py-32" data-testid="viewer-showcase-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <SectionTag>The 3D Review</SectionTag>
          <FadeIn>
            <h2 className="font-display font-black tracking-tight text-4xl sm:text-5xl leading-[1.02]">
              Review every movement <span className="text-neon">before you approve.</span>
            </h2>
          </FadeIn>
          <FadeIn delay={0.1}>
            <p className="mt-6 text-slate-400 text-sm md:text-base leading-relaxed">
              Don't approve a black-box treatment plan. Every OrthoPlaniX case opens in your portal's 3D viewer, where you stay in complete clinical control.
            </p>
          </FadeIn>
          <FadeIn delay={0.2}>
            <ul className="mt-8 grid grid-cols-2 gap-x-6 gap-y-3">
              {["Examine tooth movement", "Review staging", "Inspect attachments", "Review IPR", "Request changes", "Approve digitally"].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-sm text-slate-300">
                  <CheckCircle2 className="h-4 w-4 text-neon mt-0.5 shrink-0" /> {t}
                </li>
              ))}
            </ul>
          </FadeIn>
          <FadeIn delay={0.3}>
            <Link to="/register" data-testid="viewer-showcase-cta"
              className="group mt-10 inline-flex items-center gap-2 rounded-full bg-neon px-7 py-3.5 text-sm font-bold text-ink transition-transform duration-300 hover:scale-105 hover:shadow-[0_0_40px_rgba(0,229,255,0.45)]">
              Submit your first case <ArrowUpRight className="h-4 w-4" />
            </Link>
          </FadeIn>
        </div>
        <div className="lg:col-span-7">
          <FadeIn delay={0.15}>
            <div className="rounded-2xl border border-white/10 bg-[#0B0F19] overflow-hidden shadow-2xl">
              <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 bg-ink-2/80">
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-red-500/60" /><span className="h-2.5 w-2.5 rounded-full bg-yellow-500/60" /><span className="h-2.5 w-2.5 rounded-full bg-green-500/60" />
                </div>
                <p className="font-mono text-xs text-slate-400">Case OPX-7K2Q4M · Treatment Plan v2</p>
                <span className="rounded-full bg-neon/10 border border-neon/30 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-neon">Plan Ready</span>
              </div>
              <img src={VIEWER_IMG} alt="3D treatment plan of upper and lower arches with planned tooth movements highlighted in the OrthoPlaniX viewer" className="w-full aspect-[3/2] object-cover" />
              <div className="px-5 py-4 border-t border-white/10 bg-ink-2/80">
                <div className="flex items-center gap-4">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 shrink-0">Stage 08 / 22</p>
                  <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full w-[36%] bg-neon rounded-full" /></div>
                  <span className="hidden sm:inline-flex rounded-full border border-white/20 px-4 py-1.5 text-xs font-semibold text-slate-300">Request revision</span>
                  <span className="hidden sm:inline-flex rounded-full bg-neon px-4 py-1.5 text-xs font-bold text-ink">Approve plan</span>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>
  );
}

export function ClinicalTeam() {
  return (
    <section className="py-24 lg:py-32 bg-ink-2/40" data-testid="clinical-team-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <SectionTag>Clinical Expertise</SectionTag>
        <FadeIn>
          <h2 className="font-display font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl max-w-3xl leading-[1.02]">
            Who is behind <span className="text-neon">your treatment plan?</span>
          </h2>
        </FadeIn>
        <div className="mt-16 grid lg:grid-cols-12 gap-6">
          <FadeIn className="lg:col-span-5 h-full">
            <div className="h-full bg-white/5 border border-white/10 rounded-2xl p-8 lg:p-10">
              <div className="flex items-center gap-5">
                <div className="h-20 w-20 rounded-2xl bg-neon/10 border border-neon/40 flex items-center justify-center font-display font-black text-2xl text-neon shrink-0">PJ</div>
                <div>
                  <p className="font-display font-bold text-2xl text-white">Dr. Piyyush Joshi</p>
                  <p className="text-xs uppercase tracking-[0.2em] text-neon mt-1.5">Founder &amp; Clinical Planning Lead</p>
                </div>
              </div>
              <p className="mt-7 text-sm text-slate-400 leading-relaxed">
                12+ years in the clear-aligner industry — spanning the full workflow from digital treatment planning to aligner production. Every OrthoPlaniX case is planned under a clinical protocol he built from thousands of real-world cases.
              </p>
              <ul className="mt-7 space-y-3">
                {[
                  [Award, "12+ years across planning, workflows and aligner production"],
                  [GraduationCap, "Clinical planning protocols refined over thousands of cases"],
                  [ShieldCheck, "Senior clinical review on every plan before delivery"],
                ].map(([Icon, t]) => (
                  <li key={t} className="flex items-start gap-3 text-sm text-slate-300"><Icon className="h-4 w-4 text-neon mt-0.5 shrink-0" /> {t}</li>
                ))}
              </ul>
            </div>
          </FadeIn>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-6">
            <FadeIn delay={0.1} className="h-full">
              <div className="h-full bg-white/5 border border-white/10 rounded-2xl p-8">
                <Globe2 className="h-7 w-7 text-neon mb-5" />
                <p className="font-display font-bold text-xl">A global specialist team</p>
                <p className="text-sm text-slate-400 mt-3 leading-relaxed">
                  Behind every case: a team of specialist orthodontists from around the globe, planning to your prescription and your treatment philosophy — never a black-box algorithm.
                </p>
              </div>
            </FadeIn>
            <FadeIn delay={0.18} className="h-full">
              <div className="h-full bg-white/5 border border-white/10 rounded-2xl p-8">
                <ShieldCheck className="h-7 w-7 text-neon mb-5" />
                <p className="font-display font-bold text-xl">Clinical QC on every case</p>
                <p className="text-sm text-slate-400 mt-3 leading-relaxed">
                  No plan leaves without senior clinical review — biomechanics, staging, anchorage, attachments and IPR are checked against your stated objectives before you ever see the setup.
                </p>
              </div>
            </FadeIn>
            <FadeIn delay={0.26} className="sm:col-span-2">
              <div className="bg-neon/[0.06] border border-neon/30 rounded-2xl p-8 grid grid-cols-3 gap-6">
                {[["3000+", "Cases planned"], ["15+", "Years of experience"], ["100%", "Doctor approved"]].map(([v, l]) => (
                  <div key={l} className="text-center">
                    <p className="font-display font-black text-3xl lg:text-4xl text-neon">{v}</p>
                    <p className="text-[11px] uppercase tracking-wider text-slate-400 mt-1.5">{l}</p>
                  </div>
                ))}
              </div>
            </FadeIn>
          </div>
        </div>
      </div>
    </section>
  );
}

const SERVE = [
  { icon: Stethoscope, t: "General Dentists", d: "Start or scale clear-aligner treatment without spending hours on digital setups. Specialist planning behind every case you prescribe.", to: "/register", cta: "Submit your first case" },
  { icon: ScanFace, t: "Orthodontists", d: "Increase planning capacity and focus on diagnosis and patient care — with setups that respect real biomechanics.", to: "/register", cta: "Extend your capacity" },
  { icon: FlaskConical, t: "Dental Labs", d: "Add professional planning to your aligner workflow without hiring an internal team. Your lab manufactures — we plan.", to: "/for-labs", cta: "Planning for labs" },
  { icon: Factory, t: "Aligner Brands", d: "White-label treatment planning at scale with SLA-backed turnaround and a dedicated account team.", to: "/enterprise", cta: "Enterprise planning" },
];

export function WhoWeServe() {
  return (
    <section className="py-24 lg:py-32 bg-ink-2/40" data-testid="audiences-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <SectionTag>Who We Serve</SectionTag>
        <FadeIn>
          <h2 className="font-display font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl max-w-2xl leading-[1.02]">
            Built for the business of orthodontics.
          </h2>
        </FadeIn>
        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {SERVE.map((a, i) => (
            <FadeIn key={a.t} delay={i * 0.07} className="h-full">
              <div className="group h-full flex flex-col bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 lg:p-8 transition-all duration-500 hover:border-neon/40 hover:bg-white/[0.07]">
                <a.icon className="h-7 w-7 text-neon mb-5 transition-transform duration-500 group-hover:scale-110" />
                <p className="font-display font-bold text-lg lg:text-xl">{a.t}</p>
                <p className="text-xs text-slate-500 mt-2 leading-relaxed flex-1">{a.d}</p>
                <Link to={a.to} className="mt-5 inline-flex items-center gap-1.5 text-xs font-bold text-neon" data-testid={`serve-cta-${a.t.toLowerCase().replace(/\s/g, "-")}`}>
                  {a.cta} <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

export function CaseLibraryTeaser() {
  return (
    <section className="py-24 lg:py-32" data-testid="case-library-teaser">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-10 lg:p-14 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <SectionTag>Case Library</SectionTag>
            <FadeIn>
              <h2 className="font-display font-black tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
                See how we think, <span className="text-neon">case by case.</span>
              </h2>
            </FadeIn>
            <FadeIn delay={0.1}>
              <p className="mt-5 text-slate-400 text-sm md:text-base max-w-2xl leading-relaxed">
                Crowding with deep bite, spacing, Class II correction, open bite, extraction cases — explore anonymized treatment plans with the diagnosis, staging, attachment and IPR decisions behind each one.
              </p>
            </FadeIn>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <FadeIn delay={0.2}>
              <Link to="/case-library" data-testid="case-library-teaser-btn"
                className="group inline-flex items-center gap-2 rounded-full border border-neon/50 px-7 py-3.5 text-sm font-bold text-neon transition-colors duration-300 hover:bg-neon hover:text-ink">
                Explore the case library <ArrowUpRight className="h-4 w-4" />
              </Link>
            </FadeIn>
          </div>
        </div>
      </div>
    </section>
  );
}
